import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  ArrowBack,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle,
  RadioButtonUnchecked
} from '@mui/icons-material';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { odosActivityService, ODOSActivity, odosModuleService, ODOSModule } from '../../services/odosService';

const ODOSActivityForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditMode = id !== 'new';
  
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modules, setModules] = useState<ODOSModule[]>([]);
  
  // Form state
  const [formData, setFormData] = useState({
    activityId: '',
    title: '',
    description: '',
    module: '',
    type: 'development',
    status: 'backlog',
    priority: 'medium',
    estimatedHours: 0,
    actualHours: 0,
    dueDate: '',
  });
  
  const [acceptanceCriteria, setAcceptanceCriteria] = useState<Array<{ description: string; completed: boolean }>>([]);
  const [newCriterion, setNewCriterion] = useState('');

  useEffect(() => {
    loadModules();
    if (isEditMode) {
      loadActivity();
    }
  }, [id]);

  const loadModules = async () => {
    try {
      const response = await odosModuleService.getModules();
      setModules(response.data);
    } catch (err: any) {
      console.error('Error loading modules:', err);
      setError('Failed to load modules');
    }
  };

  const loadActivity = async () => {
    try {
      setLoading(true);
      const response = await odosActivityService.getActivity(id!);
      const activity = response.data;
      
      setFormData({
        activityId: activity.activityId,
        title: activity.title,
        description: activity.description || '',
        module: typeof activity.module === 'string' ? activity.module : activity.module._id,
        type: activity.type,
        status: activity.status,
        priority: activity.priority,
        estimatedHours: activity.estimatedHours || 0,
        actualHours: activity.actualHours || 0,
        dueDate: activity.dueDate ? new Date(activity.dueDate).toISOString().split('T')[0] : '',
      });
      
      if (activity.acceptanceCriteria) {
        setAcceptanceCriteria(activity.acceptanceCriteria);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load activity');
      console.error('Error loading activity:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddCriterion = () => {
    if (newCriterion.trim()) {
      setAcceptanceCriteria(prev => [...prev, { description: newCriterion, completed: false }]);
      setNewCriterion('');
    }
  };

  const handleToggleCriterion = (index: number) => {
    setAcceptanceCriteria(prev => 
      prev.map((criterion, i) => 
        i === index ? { ...criterion, completed: !criterion.completed } : criterion
      )
    );
  };

  const handleRemoveCriterion = (index: number) => {
    setAcceptanceCriteria(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      
      const activityData: any = {
        ...formData,
        acceptanceCriteria,
        dueDate: formData.dueDate ? new Date(formData.dueDate) : undefined,
        assignedTo: user?._id, // Set current user as assignedTo
      };
      
      if (isEditMode) {
        await odosActivityService.updateActivity(id!, activityData);
      } else {
        await odosActivityService.createActivity(activityData);
      }
      
      navigate('/odos/activities');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save activity');
      console.error('Error saving activity:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>Loading activity...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink component={Link} to="/odos" underline="hover" color="inherit">
          ODOS Dashboard
        </MuiLink>
        <MuiLink component={Link} to="/odos/activities" underline="hover" color="inherit">
          Activities
        </MuiLink>
        <Typography color="text.primary">
          {isEditMode ? 'Edit Activity' : 'New Activity'}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/odos/activities')} aria-label="Back to activities">
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ color: '#003366', fontFamily: 'Merriweather, serif' }}>
          {isEditMode ? 'Edit Activity' : 'Create New Activity'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Activity ID"
                      value={formData.activityId}
                      onChange={(e) => handleInputChange('activityId', e.target.value)}
                      placeholder="ACT-MODULE-001"
                      disabled={isEditMode}
                      helperText={isEditMode ? 'Activity ID cannot be changed' : 'Unique identifier for this activity'}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Module</InputLabel>
                      <Select
                        value={formData.module}
                        label="Module"
                        onChange={(e) => handleInputChange('module', e.target.value)}
                        disabled={modules.length === 0}
                      >
                        {modules.length === 0 ? (
                          <MenuItem value="" disabled>
                            Loading modules...
                          </MenuItem>
                        ) : (
                          modules.map((module) => (
                            <MenuItem key={module._id} value={module._id}>
                              {module.name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Title"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="Brief description of the activity"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Description"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Detailed description of the activity"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Status and Priority */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Status & Priority
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={formData.type}
                        label="Type"
                        onChange={(e) => handleInputChange('type', e.target.value)}
                      >
                        <MenuItem value="development">Development</MenuItem>
                        <MenuItem value="testing">Testing</MenuItem>
                        <MenuItem value="deployment">Deployment</MenuItem>
                        <MenuItem value="documentation">Documentation</MenuItem>
                        <MenuItem value="review">Review</MenuItem>
                        <MenuItem value="training">Training</MenuItem>
                        <MenuItem value="maintenance">Maintenance</MenuItem>
                        <MenuItem value="security-scan">Security Scan</MenuItem>
                        <MenuItem value="compliance-check">Compliance Check</MenuItem>
                        <MenuItem value="user-research">User Research</MenuItem>
                        <MenuItem value="design">Design</MenuItem>
                        <MenuItem value="integration">Integration</MenuItem>
                        <MenuItem value="migration">Migration</MenuItem>
                        <MenuItem value="monitoring">Monitoring</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={formData.status}
                        label="Status"
                        onChange={(e) => handleInputChange('status', e.target.value)}
                      >
                        <MenuItem value="backlog">Backlog</MenuItem>
                        <MenuItem value="planned">Planned</MenuItem>
                        <MenuItem value="in-progress">In Progress</MenuItem>
                        <MenuItem value="blocked">Blocked</MenuItem>
                        <MenuItem value="review">Review</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Priority</InputLabel>
                      <Select
                        value={formData.priority}
                        label="Priority"
                        onChange={(e) => handleInputChange('priority', e.target.value)}
                      >
                        <MenuItem value="critical">Critical</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="low">Low</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Time Tracking */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Time Tracking
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Estimated Hours"
                      value={formData.estimatedHours}
                      onChange={(e) => handleInputChange('estimatedHours', parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, step: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Actual Hours"
                      value={formData.actualHours}
                      onChange={(e) => handleInputChange('actualHours', parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, step: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Due Date"
                      value={formData.dueDate}
                      onChange={(e) => handleInputChange('dueDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Acceptance Criteria */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Acceptance Criteria
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs>
                      <TextField
                        fullWidth
                        size="small"
                        label="New Criterion"
                        value={newCriterion}
                        onChange={(e) => setNewCriterion(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCriterion();
                          }
                        }}
                        placeholder="Enter acceptance criterion"
                      />
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddCriterion}
                        sx={{ bgcolor: '#003366' }}
                      >
                        Add
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
                
                {acceptanceCriteria.length > 0 ? (
                  <List>
                    {acceptanceCriteria.map((criterion, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={criterion.completed}
                                onChange={() => handleToggleCriterion(index)}
                                icon={<RadioButtonUnchecked />}
                                checkedIcon={<CheckCircle color="success" />}
                              />
                            }
                            label={
                              <Typography
                                sx={{
                                  textDecoration: criterion.completed ? 'line-through' : 'none',
                                  color: criterion.completed ? 'text.secondary' : 'text.primary'
                                }}
                              >
                                {criterion.description}
                              </Typography>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() => handleRemoveCriterion(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < acceptanceCriteria.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Alert severity="info">
                    No acceptance criteria defined. Add criteria to track completion requirements.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/odos/activities')}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saving}
                sx={{ bgcolor: '#003366', '&:hover': { bgcolor: '#002244' } }}
              >
                {saving ? 'Saving...' : isEditMode ? 'Update Activity' : 'Create Activity'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default ODOSActivityForm;
